import { cn } from '@/lib/cn';

/**
 * Logotipo de CaliPeak.
 *
 * Son DOS piezas de la misma ilustracion, y cual se usa depende del tamano:
 *
 *   - `mark`  el escudo con el atleta en front lever, recortado en circulo.
 *             Es el que va en la cabecera, el favicon y los iconos.
 *   - `full`  el guerrero entero con lanza, capa y escudo. Solo donde hay
 *             sitio: la portada, la animacion de entrada, la imagen social.
 *
 * El reparto no es estetico sino de legibilidad. El guerrero completo tiene
 * lanza, penacho, capa y escudo; por debajo de unos 64 px todo eso se funde en
 * una mancha. El escudo es un circulo con una sola figura dentro y aguanta
 * hasta 16 px, que es donde vive un favicon.
 *
 * Antes esto era un SVG dibujado a mano. Lo sustituye la ilustracion real de
 * la marca, que vive en `public/` y de la que salen todos los iconos
 * (`npm run icons`).
 */

interface SpartanLogoProps {
  /** `mark` por defecto: es lo que pide casi todo el sitio. */
  variant?: 'mark' | 'full';
  className?: string;
  /** Sin titulo, el logotipo es decorativo y se oculta al lector de pantalla. */
  title?: string;
  /**
   * Ancho al que se pinta, para que el navegador elija la version ligera
   * adecuada del `srcSet` (ver scripts/web-images.mjs).
   */
  sizes?: string;
  /** La imagen principal de la pantalla (LCP): se pide antes que el resto. */
  priority?: boolean;
}

// Versiones WebP generadas con `npm run images`. Los PNG originales pesan
// 1,2 MB (guerrero) y 270 KB (escudo) y se pintan a menos de 210 px.
const SOURCES = {
  full: {
    src: '/img/logo-480.webp',
    srcSet: '/img/logo-320.webp 320w, /img/logo-480.webp 480w, /img/logo-800.webp 800w',
    sizes: '208px',
    // Proporcion real de logo.png (963x894): reserva el hueco antes de cargar.
    width: 963,
    height: 894,
  },
  mark: {
    src: '/img/mark-96.webp',
    srcSet: '/img/mark-48.webp 48w, /img/mark-96.webp 96w, /img/mark-192.webp 192w',
    sizes: '40px',
    width: 360,
    height: 360,
  },
} as const;

export function SpartanLogo({
  variant = 'mark',
  className,
  title,
  sizes,
  priority = false,
}: SpartanLogoProps) {
  const source = SOURCES[variant];

  return (
    <img
      src={source.src}
      srcSet={source.srcSet}
      sizes={sizes ?? source.sizes}
      width={source.width}
      height={source.height}
      alt={title ?? ''}
      aria-hidden={title ? undefined : true}
      // `contain` para que nunca se deforme sea cual sea la caja que reciba:
      // el guerrero no es cuadrado y la marca si.
      className={cn('block select-none object-contain', className)}
      draggable={false}
      // Las dos van en `eager`. El guerrero se usa en exactamente dos sitios
      // —la portada y la animacion de entrada— y en los dos esta sobre el
      // pliegue: diferirlo solo consigue que aparezca tarde justo donde mas se
      // mira.
      loading="eager"
      decoding={priority ? 'sync' : 'async'}
      // React 18 aun no conoce `fetchPriority`; en minusculas llega tal cual.
      {...(priority ? { fetchpriority: 'high' } : {})}
    />
  );
}
